import React, { useState } from "react";
import { useTranslation } from "utilities/i18n";
import { FeatureFlagEnabled, Loader, PolyInternalBadge } from "elements";
import { columnTranslations, PrimaryDeviceInfo } from "./PDIHelpers";
import { filterDeviceInfoOnString } from "./DeviceInformation";
import {
  DeviceFragment,
  useDeviceDetailsQuery,
  usePrimaryDeviceInfoLinesQuery
} from "generated/graphql";
import { constructFilterList } from "utilities/filters/deviceFilters";

interface PDISectionProps {
  searchState: string;
  deviceId: string;
}

const context = {
  maskTypename: true
};

function extractValues(pdiObj: any) {
  const pdi: Partial<PrimaryDeviceInfo> = {};
  for (const key in pdiObj) {
    const value = pdiObj[key]?.value ? pdiObj[key]?.value : null;
    pdi[key as keyof PrimaryDeviceInfo] = value;
  }
  return pdi;
}

export const PrimaryDeviceInfoSection: React.FC<PDISectionProps> = ({
  searchState,
  deviceId
}) => {
  const { data, fetching } = usePrimaryDeviceInfoLinesQuery({
    variables: {
      deviceId
    },
    context
  });
  const { data: fallbackData } = useDeviceDetailsQuery({
    variables: {
      filter: {
        AND: constructFilterList({ id: deviceId })
      }
    }
  });
  const fallback: Partial<DeviceFragment> = fallbackData?.devices[0] ?? {};
  // extracting values from each object
  // i.e. deviceid.value will be deviceid directly
  const pdiData = extractValues(
    data?.capabilitiesQuery?.com?.poly?.primary_device_information ?? {}
  );
  const withFallback: PrimaryDeviceInfo = {
    deviceid: deviceId,
    macaddress: fallback?.macAddress ?? undefined,
    softwarebuild: fallback?.softwareBuild ?? undefined,
    hardwaremodel: fallback?.hardwareModel ?? undefined,
    serialnumber: fallback?.serialNumber ?? undefined,
    ...pdiData
  };

  const pdiDataLength = Object.keys(pdiData).length;
  const [showNullItems, setShowNullItems] = useState(false);
  const { t } = useTranslation();
  function mapPrimaryDeviceInfoItem(key: any, value: any) {
    const keyName = key as keyof PrimaryDeviceInfo;
    return (
      <div className="pdi-field-grouping" key={`${keyName}-${value}`}>
        <label className="pdi-field-label">{`${columnTranslations[keyName]}:`}</label>
        <p className="pdi-field-value">{value}</p>
      </div>
    );
  }

  return (
    <div className="primary-device-info" data-testid="primary-device-info">
      <Loader loading={fetching}>
        {pdiDataLength > 0 && (
          <section className="pdi-data" data-testid="pdi-section">
            {filterDeviceInfoOnString(searchState, withFallback, "pdi")
              .filter(([_, value]) => value !== null)
              .map(([key, value]) => mapPrimaryDeviceInfoItem(key, value))}
            <FeatureFlagEnabled feature="IS_POLY_USER">
              <div
                className="show-null"
                onClick={() => setShowNullItems(!showNullItems)}
                data-testid="toggle-pdi-null-items"
              >
                {showNullItems
                  ? t("HIDE_NULL_INFORMATION")
                  : t("SHOW_NULL_INFORMATION")}
              </div>
              {<PolyInternalBadge />}
              {showNullItems && (
                <div data-testid="pdi-null-items">
                  {filterDeviceInfoOnString(searchState, withFallback, "pdi")
                    .filter(([_, value]) => value === null)
                    .map(([key, value]) =>
                      mapPrimaryDeviceInfoItem(key, value)
                    )}
                </div>
              )}
            </FeatureFlagEnabled>
          </section>
        )}
      </Loader>
    </div>
  );
};
